import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Input from "../../theme/untitled/Input/Input";
import ConfirmDialog from "../../theme/untitled/ConfirmDialog/ConfirmDialog";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./WorkspacePage.module.scss";
import { parseGeneralError } from "./errorHelpers";

const HELP_ARTICLE_URL =
  "https://help.revisual.io/en/articles/13610143-how-to-delete-your-revisual-account";

const DeleteWorkspaceCard = ({ orgModel, currentOrganization, deleteOrganization }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  const deleting = currentOrganization.deleteOrganization;

  useEffect(() => {
    if (currentOrganization.deleteOrganizationError) {
      setConfirmOpen(false);
    }
  }, [currentOrganization.deleteOrganizationError]);

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setConfirmName("");
  };

  const onConfirmDelete = () => {
    deleteOrganization(orgModel.uuid);
  };

  const errorMessage = currentOrganization.deleteOrganizationError
    ? parseGeneralError(
        currentOrganization.deleteOrganizationError,
        "The service is unavailable. Try again in a moment."
      )
    : null;

  return (
    <Card aria-labelledby="delete-workspace-title">
      <Card.Header
        className={classNames(styles.clickableHeader, {
          [styles.noHeaderBorder]: !isOpen,
        })}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className={styles.deleteHeader}>
          <div className={styles.deleteHeaderText}>
            <h2 id="delete-workspace-title">Delete workspace</h2>
            <p>
              Removes this workspace and everything in it.{" "}
              <a
                className={styles.deleteLink}
                href={HELP_ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                How workspace deletion works
                <Icon name="Share04" size={14} className={styles.deleteLinkIcon} />
              </a>
            </p>
          </div>
          <Button
            hierarchy="secondaryGray"
            size="sm"
            aria-expanded={isOpen}
            iconTrailing={
              <Icon
                name="ChevronDown"
                size={16}
                className={`${styles.disclosureChevron} ${
                  isOpen ? styles["disclosureChevron--open"] : ""
                }`}
              />
            }
            className={styles.disclosureButton}
          >
            <span className={styles.disclosureLabel}>
              {isOpen ? "Hide details" : "Show details"}
            </span>
          </Button>
        </div>
      </Card.Header>

      {isOpen && (
        <>
          <Card.Body>
            <div className={styles.alert}>
              <Icon name="AlertTriangle" size={20} className={styles.alertIcon} />
              <div className={styles.alertText}>
                <strong>This is permanent.</strong> Workspace data, integrations, widgets and
                connected websites cannot be restored.
              </div>
            </div>
            <div className={styles.prose}>
              <p className={styles.eyebrow}>Before you can delete this workspace</p>
              <ul>
                <li>
                  Cancel the active subscription in{" "}
                  <Link
                    to="/organization-settings/billing"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Billing
                  </Link>
                </li>
                <li>
                  Remove the other members, or promote one of them to <strong>Owner</strong>
                </li>
              </ul>
              <p className={styles.eyebrow}>What is removed</p>
              <ul>
                <li>All workspace data, integrations, widgets and connected websites</li>
                <li>Financial records are kept for as long as the law requires</li>
              </ul>
              <p>
                You are moved to another workspace as soon as deletion completes. If this is
                your only workspace,{" "}
                <Link to="/settings" onClick={(e) => e.stopPropagation()}>
                  delete your account
                </Link>{" "}
                instead — a workspace cannot exist without one.
              </p>
            </div>
          </Card.Body>
          <Card.Footer className={styles.cardFooter}>
            {errorMessage ? (
              <span className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}>
                <Icon
                  name="AlertCircle"
                  size={16}
                  className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
                />
                <span>
                  <strong>Couldn't delete this workspace.</strong> {errorMessage}
                </span>
              </span>
            ) : (
              <span className={styles.footerNote}>
                Nothing is blocking deletion. This cannot be undone.
              </span>
            )}
            <div className={styles.cardFooterActions}>
              <Button hierarchy="destructive" onClick={() => setConfirmOpen(true)}>
                {errorMessage ? "Try again" : "Delete workspace"}
              </Button>
            </div>
          </Card.Footer>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={onConfirmDelete}
        icon={<Icon name="AlertTriangle" size={24} />}
        title="Delete this workspace?"
        confirmLabel={deleting ? "Deleting…" : "Delete workspace"}
        confirmDisabled={confirmName !== orgModel.name}
        busy={deleting}
      >
        <p>This cannot be undone. Type the workspace name to confirm.</p>
        <div className={styles.confirmField}>
          <Input
            label="Confirm the workspace name"
            hint={orgModel.name}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            disabled={deleting}
            autoComplete="off"
          />
        </div>
      </ConfirmDialog>
    </Card>
  );
};

export default DeleteWorkspaceCard;
